import _extends from "@babel/runtime/helpers/esm/extends";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";

var _class, _class2, _temp;

import React from 'react';
import warning from 'warning';
import isEqual from 'lodash.isequal';
import { globalObject } from '@skbkontur/global-object';
import { isNullable } from "../../../lib/utils";
import { ThemeFactory } from "../../../lib/theming/ThemeFactory";
import { DefaultPosition, Popup } from "../../../internal/Popup";
import { RenderLayer } from "../../../internal/RenderLayer";
import { CrossIcon } from "../../../internal/icons/CrossIcon";
import { containsTargetOrRenderContainer } from "../../../lib/listenFocusOutside";
import { ThemeContext } from "../../../lib/theming/ThemeContext";
import { isTestEnv } from "../../../lib/currentEnvironment";
import { CommonWrapper } from "../../../internal/CommonWrapper";
import { rootNode } from "../../../lib/rootNode";
import { createPropsGetter } from "../../../lib/createPropsGetter";
import { isTheme2022 } from "../../../lib/theming/ThemeHelpers";
import { CloseButtonIcon } from "../../../internal/CloseButtonIcon/CloseButtonIcon";
import { isInstanceOf } from "../../../lib/isInstanceOf";
import { styles } from "../Tooltip.styles";
var DEFAULT_DELAY = 100;
export var TooltipDataTids = {
  root: 'Tooltip__root',
  content: 'Tooltip__content',
  crossIcon: 'Tooltip__crossIcon'
};
var Positions = ['right bottom', 'right middle', 'right top', 'top right', 'top center', 'top left', 'left top', 'left middle', 'left bottom', 'bottom left', 'bottom center', 'bottom right'];
export var Tooltip = rootNode(_class = (_temp = _class2 = /*#__PURE__*/function (_React$PureComponent) {
  _inheritsLoose(Tooltip, _React$PureComponent);

  function Tooltip() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$PureComponent.call.apply(_React$PureComponent, [this].concat(args)) || this;
    _this.getProps = createPropsGetter(Tooltip.defaultProps);
    _this.state = {
      opened: false,
      focused: false
    };
    _this.contentElement = null;
    _this.positions = null;
    _this.clickedOutside = true;
    _this.popupRef = /*#__PURE__*/React.createRef();

    _this.renderContent = function () {
      var content = _this.props.render ? _this.props.render() : null;

      if (isNullable(content)) {
        return null;
      }

      return /*#__PURE__*/React.createElement("div", {
        ref: _this.refContent,
        className: styles.tooltipContent(_this.theme),
        "data-tid": TooltipDataTids.content
      }, content, _this.renderCloseButton());
    };

    _this.getAnchorElement = function () {
      var _this$popupRef$curren;

      return (_this$popupRef$curren = _this.popupRef.current) == null ? void 0 : _this$popupRef$curren.anchorElement;
    };

    _this.refContent = function (node) {
      _this.contentElement = node;
    };

    _this.open = function () {
      return _this.setState({
        opened: true
      });
    };

    _this.close = function () {
      return _this.setState({
        opened: false
      });
    };

    _this.handleMouseEnter = function (event) {
      var isHoverAnchor = _this.getProps().trigger === 'hoverAnchor';

      if (isHoverAnchor && event.target === _this.contentElement) {
        return;
      }

      _this.clearHoverTimeout();

      _this.hoverTimeout = globalObject.setTimeout(_this.open, _this.getProps().delayBeforeShow);
    };

    _this.handleMouseLeave = function (event) {
      var trigger = _this.getProps().trigger;

      if (trigger === 'hover&focus' && _this.state.focused || trigger === 'hover' && event.relatedTarget === _this.contentElement) {
        return;
      }

      _this.clearHoverTimeout();

      if (trigger === 'hoverAnchor') {
        _this.close();
      } else {
        _this.hoverTimeout = globalObject.setTimeout(_this.close, Tooltip.delay);
      }
    };

    _this.handleClick = function () {
      _this.open();
    };

    _this.handleClickOutsideAnchor = function (event) {
      _this.clickedOutside = _this.isClickOutsideContent(event);

      if (_this.clickedOutside) {
        if (_this.props.onCloseRequest) {
          _this.props.onCloseRequest(event);
        }

        _this.close();
      }
    };

    _this.handleFocus = function () {
      _this.setState({
        focused: true
      });

      _this.open();
    };

    _this.handleBlur = function () {
      var trigger = _this.getProps().trigger;

      if (trigger === 'hover&focus' && _this.clickedOutside) {
        _this.close();
      }

      if (trigger === 'focus') {
        _this.close();
      }

      _this.clickedOutside = true;

      _this.setState({
        focused: false
      });
    };

    _this.handleCloseButtonClick = function (event) {
      event.stopPropagation();

      if (_this.props.onCloseClick) {
        _this.props.onCloseClick(event);
      }

      if (event.defaultPrevented) {
        return;
      }

      if (_this.props.onCloseRequest) {
        _this.props.onCloseRequest(event);
      }

      _this.close();
    };

    return _this;
  }

  var _proto = Tooltip.prototype;

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    var _this$getProps = this.getProps(),
        trigger = _this$getProps.trigger,
        allowedPositions = _this$getProps.allowedPositions,
        pos = _this$getProps.pos;

    if (trigger === 'closed' && this.state.opened) {
      this.close();
    }

    var posChanged = prevProps.pos !== pos;
    var allowedChanged = !isEqual(prevProps.allowedPositions, allowedPositions);

    if (posChanged || allowedChanged) {
      this.positions = null;
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    this.clearHoverTimeout();
  };

  _proto.render = function render() {
    var _this2 = this;

    return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, function (theme) {
      _this2.theme = theme;
      return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
        value: ThemeFactory.create({
          popupPinOffset: theme.tooltipPinOffset,
          popupMargin: theme.tooltipMargin,
          popupBorder: theme.tooltipBorder,
          popupBorderRadius: theme.tooltipBorderRadius,
          popupPinSize: theme.tooltipPinSize,
          popupPinOffsetX: theme.tooltipPinOffsetX,
          popupPinOffsetY: theme.tooltipPinOffsetY,
          popupBackground: theme.tooltipBg
        }, theme)
      }, _this2.renderMain());
    });
  };

  _proto.renderCloseButton = function renderCloseButton() {
    var hasCross = this.props.closeButton === undefined ? !Tooltip.triggersWithoutCloseButton.includes(this.getProps().trigger) : this.props.closeButton;

    if (!hasCross) {
      return null;
    }

    var icon = isTheme2022(this.theme) ? /*#__PURE__*/React.createElement(CloseButtonIcon, {
      tabbable: false,
      side: parseInt(this.theme.tooltipCloseBtnSide),
      color: this.theme.tooltipCloseBtnColor,
      colorHover: this.theme.tooltipCloseBtnHoverColor
    }) : /*#__PURE__*/React.createElement(CrossIcon, null);
    return /*#__PURE__*/React.createElement("div", {
      className: styles.cross(this.theme),
      onClick: this.handleCloseButtonClick,
      "data-tid": TooltipDataTids.crossIcon
    }, icon);
  };
  /**
  * Программно открывает тултип.
  * <p>Не действует если проп *trigger* `'opened'` или `'closed'`.</p>
  * @public
  */


  _proto.show = function show() {
    if (this.state.opened) {
      return;
    }

    var trigger = this.getProps().trigger;

    if (trigger === 'opened' || trigger === 'closed') {
      warning(true, "Function 'show' is not supported with trigger specified '" + trigger + "'");
      return;
    }

    this.open();
  }
  /**
  * Программно закрывает тултип.
  * <p>Не действует если проп *trigger* `'opened'` или `'closed'`.</p>
  * @public
  */
  ;

  _proto.hide = function hide() {
    var trigger = this.getProps().trigger;

    if (trigger === 'opened' || trigger === 'closed') {
      warning(true, "Function 'hide' is not supported with trigger specified '" + trigger + "'");
      return;
    }

    this.close();
  };

  _proto.renderMain = function renderMain() {
    var props = this.props;
    var content = this.renderContent();

    var _this$getPopupAndLaye = this.getPopupAndLayerProps(),
        popupProps = _this$getPopupAndLaye.popupProps,
        _this$getPopupAndLaye2 = _this$getPopupAndLaye.layerProps,
        layerProps = _this$getPopupAndLaye2 === void 0 ? {
      active: false
    } : _this$getPopupAndLaye2;

    var anchorElement = props.children || props.anchorElement;
    var popup = this.renderPopup(anchorElement, popupProps, content);
    return /*#__PURE__*/React.createElement(RenderLayer, _extends({}, layerProps, {
      getAnchorElement: this.getAnchorElement
    }), popup);
  };

  _proto.renderPopup = function renderPopup(anchorElement, popupProps, content) {
    var _this$getProps2 = this.getProps(),
        disableAnimations = _this$getProps2.disableAnimations,
        trigger = _this$getProps2.trigger;

    return /*#__PURE__*/React.createElement(CommonWrapper, _extends({
      rootNodeRef: this.setRootNode
    }, this.props), /*#__PURE__*/React.createElement(Popup, _extends({
      "data-tid": TooltipDataTids.root,
      anchorElement: anchorElement,
      hasPin: true,
      hasShadow: true,
      maxWidth: "none",
      opened: this.state.opened,
      disableAnimations: disableAnimations,
      positions: this.getPositions(),
      ignoreHover: trigger === 'hoverAnchor',
      onOpen: this.props.onOpen,
      onClose: this.props.onClose,
      tryPreserveFirstRenderedPosition: true,
      ref: this.popupRef,
      withoutMobile: true
    }, popupProps), content));
  };

  _proto.getPositions = function getPositions() {
    if (!this.positions) {
      var _this$getProps3 = this.getProps(),
          allowedPositions = _this$getProps3.allowedPositions,
          pos = _this$getProps3.pos;

      var index = allowedPositions.indexOf(pos);

      if (index === -1) {
        throw new Error('Unexpected position passed to Tooltip. Expected one of: ' + allowedPositions.join(', '));
      }

      this.positions = [].concat(allowedPositions.slice(index), allowedPositions.slice(0, index));
    }

    return this.positions;
  };

  _proto.getPopupAndLayerProps = function getPopupAndLayerProps() {
    var props = this.props;
    var useWrapper = !!props.children && this.getProps().useWrapper;
    var trigger = this.getProps().trigger;

    switch (trigger) {
      case 'opened':
        return {
          layerProps: {
            active: true,
            onClickOutside: this.handleClickOutsideAnchor
          },
          popupProps: {
            opened: true,
            useWrapper: useWrapper
          }
        };

      case 'closed':
        return {
          popupProps: {
            opened: false,
            useWrapper: useWrapper
          }
        };

      case 'hoverAnchor':
      case 'hover':
        return {
          popupProps: {
            onMouseEnter: this.handleMouseEnter,
            onMouseLeave: this.handleMouseLeave,
            useWrapper: useWrapper
          }
        };

      case 'manual':
        return {
          popupProps: {
            useWrapper: useWrapper
          }
        };

      case 'click':
        return {
          layerProps: {
            active: this.state.opened,
            onClickOutside: this.handleClickOutsideAnchor
          },
          popupProps: {
            onClick: this.handleClick,
            useWrapper: useWrapper
          }
        };

      case 'focus':
        return {
          popupProps: {
            onFocus: this.handleFocus,
            onBlur: this.handleBlur,
            useWrapper: useWrapper
          }
        };

      case 'hover&focus':
        return {
          layerProps: {
            active: this.state.opened,
            onClickOutside: this.handleClickOutsideAnchor
          },
          popupProps: {
            onFocus: this.handleFocus,
            onBlur: this.handleBlur,
            onMouseEnter: this.handleMouseEnter,
            onMouseLeave: this.handleMouseLeave,
            useWrapper: useWrapper
          }
        };

      default:
        throw new Error('Unknown trigger specified: ' + trigger);
    }
  };

  _proto.clearHoverTimeout = function clearHoverTimeout() {
    if (this.hoverTimeout) {
      globalObject.clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  };

  _proto.isClickOutsideContent = function isClickOutsideContent(event) {
    if (this.contentElement && isInstanceOf(event.target, globalObject.Element)) {
      return !containsTargetOrRenderContainer(event.target)(this.contentElement);
    }

    return true;
  };

  return Tooltip;
}(React.PureComponent), _class2.__KONTUR_REACT_UI__ = 'Tooltip', _class2.displayName = 'Tooltip', _class2.propTypes = {
  children: function children(props, propName, componentName) {
    var children = props[propName];
    warning(children || props.anchorElement, "[" + componentName + "]: you must provide either 'children' or 'anchorElement' prop for " + componentName + " to work properly");
    warning(!(Array.isArray(children) && props.useWrapper === false), "[" + componentName + "]: you provided multiple children, but useWrapper={false} - forcing wrapper <span/> for positioning to work correctly");
  }
}, _class2.defaultProps = {
  pos: DefaultPosition,
  trigger: 'hover',
  allowedPositions: Positions,
  disableAnimations: isTestEnv,
  useWrapper: false,
  delayBeforeShow: DEFAULT_DELAY
}, _class2.delay = DEFAULT_DELAY, _class2.triggersWithoutCloseButton = ['hover', 'hoverAnchor', 'focus', 'hover&focus'], _temp)) || _class;